import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { setCookie, getCookie, deleteCookie } from '../utils/cookies';

const DashboardAuthContext = createContext(null);

export function DashboardAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasInitialized = useRef(false);

  // Clear auth function (defined early so it can be used in useEffect)
  const clearAuth = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('dashboardAdminToken');
    localStorage.removeItem('dashboardAdmin');
    
    // Clear cookies
    deleteCookie('dashboardAdminToken');
    deleteCookie('dashboardAdmin');
    
    // Clear auth header
    delete api.defaults.headers.common['Authorization'];
    
    setAdmin(null);
    setPermissions([]);
    setIsAuthenticated(false);
  }, []);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!admin) return false;
    // Owner and super_admin have all permissions
    if (admin.role === 'owner' || admin.role === 'super_admin') return true;
    if (permissions === 'all') return true;
    return Array.isArray(permissions) && permissions.includes(permission);
  }, [admin, permissions]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissionList) => {
    if (!admin) return false;
    if (admin.role === 'owner' || admin.role === 'super_admin') return true;
    if (permissions === 'all') return true;
    return permissionList.some(p => hasPermission(p));
  }, [admin, permissions, hasPermission]);

  // Check if user is owner
  const isOwner = useCallback(() => {
    return admin?.role === 'owner';
  }, [admin]);

  // Check if user is super admin or owner
  const isSuperAdmin = useCallback(() => {
    return admin?.role === 'owner' || admin?.role === 'super_admin';
  }, [admin]);

  // Check if user can manage admins
  const canManageAdmins = useCallback(() => {
    return isSuperAdmin() || hasPermission('admins.manage');
  }, [isSuperAdmin, hasPermission]);

  // Load admin from storage on mount
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const loadAdmin = async () => {
      try {
        let token = localStorage.getItem('dashboardAdminToken');
        let adminData = localStorage.getItem('dashboardAdmin');

        // Fallback to cookies
        if (!token) {
          token = getCookie('dashboardAdminToken');
          adminData = getCookie('dashboardAdmin');
        }

        if (token && adminData && adminData !== 'undefined') {
          try {
            const parsed = JSON.parse(adminData);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            // Verify token is still valid by fetching current admin info
            try {
              const response = await api.get('/dashboard-admin/me');
              setAdmin(response.data);
              setPermissions(response.data.permissions || []);
              setIsAuthenticated(true);
            } catch (err) {
              // Token invalid, clear auth
              console.error('Token validation failed:', err);
              clearAuth();
            }
          } catch (parseErr) {
            console.error('Error parsing admin data:', parseErr);
            clearAuth();
          }
        }
      } catch (err) {
        console.error('Error loading admin:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAdmin();
  }, [clearAuth]);

  // Heartbeat - keep last_seen updated for online status
  useEffect(() => {
    if (!isAuthenticated || !admin) return;

    // Send initial heartbeat
    api.post('/dashboard-admin/heartbeat').catch(() => {});

    // Then every 60 seconds
    const interval = setInterval(() => {
      api.post('/dashboard-admin/heartbeat').catch(() => {});
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, admin]);

  // Login function
  const login = async (email, password, totpCode = null) => {
    try {
      const response = await api.post('/dashboard-admin/login', { email, password, totpCode });
      
      // Check if 2FA is required
      if (response.data.requires2FA) {
        return { success: false, requires2FA: true };
      }
      
      if (response.data.success && response.data.accessToken) {
        const { accessToken, admin: adminData } = response.data;
        
        // Store in localStorage
        localStorage.setItem('dashboardAdminToken', accessToken);
        localStorage.setItem('dashboardAdmin', JSON.stringify(adminData));
        
        // Store in cookies for persistence
        setCookie('dashboardAdminToken', accessToken, 7);
        setCookie('dashboardAdmin', JSON.stringify(adminData), 7);
        
        // Set auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        setAdmin(adminData);
        setPermissions(adminData.permissions || []);
        setIsAuthenticated(true);
        
        return { success: true };
      }
      
      return { success: false, error: response.data.error || 'Login failed' };
    } catch (err) {
      // Login error handled
      return { 
        success: false, 
        error: err.response?.data?.error || 'Erreur de connexion' 
      };
    }
  };

  // Logout function - calls backend to clear last_seen, then clears local auth
  const logout = useCallback(async () => {
    try {
      await api.post('/dashboard-admin/logout');
    } catch (e) {
      // Ignore errors - still proceed with local logout
    }
    clearAuth();
  }, [clearAuth]);

  // Refresh admin data
  const refreshAdmin = async () => {
    try {
      const response = await api.get('/dashboard-admin/me');
      setAdmin(response.data);
      setPermissions(response.data.permissions || []);
      
      // Update storage
      localStorage.setItem('dashboardAdmin', JSON.stringify(response.data));
      setCookie('dashboardAdmin', JSON.stringify(response.data), 7);
    } catch (err) {
      console.error('Error refreshing admin:', err);
    }
  };

  const value = {
    admin,
    permissions,
    loading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    isOwner,
    isSuperAdmin,
    canManageAdmins,
    refreshAdmin
  };

  return (
    <DashboardAuthContext.Provider value={value}>
      {children}
    </DashboardAuthContext.Provider>
  );
}

export function useDashboardAuth() {
  const context = useContext(DashboardAuthContext);
  if (!context) {
    throw new Error('useDashboardAuth must be used within DashboardAuthProvider');
  }
  return context;
}

// HOC for protecting routes
export function withPermission(WrappedComponent, requiredPermission) {
  return function PermissionGuard(props) {
    const { hasPermission, loading, isAuthenticated } = useDashboardAuth();

    if (loading) {
      return <div className="loading">Chargement...</div>;
    }

    if (!isAuthenticated) {
      return <div className="access-denied">Non authentifié</div>;
    }

    if (!hasPermission(requiredPermission)) {
      return (
        <div className="access-denied">
          <h2>Accès refusé</h2>
          <p>Vous n'avez pas la permission d'accéder à cette page.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
}

export default DashboardAuthContext;
